'use client'

import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    naver?: {
      maps?: {
        Size: new (width: number, height: number) => unknown
        LatLng: new (lat: number, lng: number) => unknown
        Point: new (x: number, y: number) => unknown
        Map: new (
          el: HTMLElement,
          options: {
            center: unknown
            zoom: number
            draggable?: boolean
            zoomControl?: boolean
            zoomControlOptions?: { position: unknown }
            disableDoubleClickZoom?: boolean
            doubleTapZoom?: boolean
            twoFingerTapZoom?: boolean
            pinchZoom?: boolean
            scrollWheel?: boolean
          },
        ) => unknown
        Marker: new (options: {
          position: unknown
          map?: unknown
          icon?: {
            url: string
            scaledSize?: unknown
            anchor?: unknown
            size?: unknown
            origin?: unknown
          }
        }) => unknown
        Position: {
          TOP_RIGHT: unknown
        }
        Service?: {
          geocode?: (
            request: { query: string },
            callback: (status: unknown, response: unknown) => void,
          ) => void
        }
      }
    }
  }
}

interface NaverMapProps {
  location: {
    latitude: number
    longitude: number
  },
  width?: string
  height?: string
  zoom?: number
  zoomControl?: boolean
  disableDoubleClickZoom?: boolean
  draggable?: boolean
  scrollWheel?: boolean
  pinchZoom?: boolean
  disableDoubleTapZoom?: boolean
  disableTwoFingerTapZoom?: boolean
  className?: string
}

const NaverMap = ({
  location,
  width = '100%',
  height = '100px',
  zoom = 17,
  zoomControl = false,
  draggable = false,
  disableDoubleClickZoom = true,
  disableDoubleTapZoom = true,
  disableTwoFingerTapZoom = true,
  scrollWheel = false,
  pinchZoom = false,
  className = '',
}: NaverMapProps) => {
  const mapElRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<unknown>(null)
  const [ready, setReady] = useState(() => {
    if (typeof window === 'undefined') return false
    return Boolean(window.naver?.maps)
  })

  useEffect(() => {
    if (!ready) return
    if (!mapElRef.current) return
    if (!window.naver?.maps) return

    const center = new window.naver.maps.LatLng(location.latitude, location.longitude)

    const nextOptions = {
      center,
      zoom,
      draggable,
      zoomControl,
      scrollWheel,
      pinchZoom,
      disableDoubleClickZoom,
      disableDoubleTapZoom,
      disableTwoFingerTapZoom,
    }

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.naver.maps.Map(mapElRef.current, nextOptions)
    } else {
      ;(mapInstanceRef.current as { setOptions?: (opts: typeof nextOptions) => void }).setOptions?.(nextOptions)
    }

    new window.naver.maps.Marker({
      position: center,
      map: mapInstanceRef.current,
      icon: {
        url: '/icons/marker.png',
        size: new window.naver.maps.Size(42, 42),
        scaledSize: new window.naver.maps.Size(42, 42),
        origin: new window.naver.maps.Point(0, 0),
        anchor: new window.naver.maps.Point(12, 34)
      }
    })
  }, [
    ready,
    location,
    zoom,
    draggable,
    zoomControl,
    disableDoubleClickZoom,
    disableDoubleTapZoom,
    disableTwoFingerTapZoom,
    scrollWheel,
    pinchZoom,
  ])

  return (
    <>
      {process.env.NEXT_PUBLIC_NAVER_MAPS_KEY && <Script
        src={`https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.NEXT_PUBLIC_NAVER_MAPS_KEY}&submodules=geocoder`}
        strategy="afterInteractive"
        onLoad={() => setReady(true)}
        onReady={() => setReady(true)}
      />}
      <div
        ref={mapElRef}
        className={`w-full h-full ${className}`}
        style={{ width, height }}
      />
    </>
  )
}

export default NaverMap
